import type { AutomationFormSchema } from "../hooks/use-automation-form-schema";
import type { AutomationEditData } from "../types";

type FieldRow = {
  title: string;
  key: string;
  value: string;
  target_type: string;
  data_type: string;
};

const mapBodyField = (
  title: string,
  key: string,
  value: string,
  targetType: string | null | undefined,
  dataType?: string
): FieldRow => ({
  title,
  key,
  value,
  target_type: (targetType ?? "body") as string,
  data_type: dataType || "string"
});

const buildEndpointFromStep = (
  step: AutomationEditData["steps"][number]
): AutomationFormSchema["endpoints"][number] => {
  const mappings = step.mappings ?? [];

  const fillCustomer = mappings
    .filter((m) => m.mapping_type === "dynamic")
    .map((m) =>
      mapBodyField(m.title, m.key, m.value, m.target_type, m.value_type)
    );

  const staticSystem = mappings
    .filter((m) => m.mapping_type === "static")
    .map((m) =>
      mapBodyField(m.title, m.key, m.value, m.target_type, m.value_type)
    );

  const staticValue = mappings
    .filter((m) => m.mapping_type === "system")
    .map((m) =>
      mapBodyField(m.title, m.key, m.value, m.target_type, m.value_type)
    );

  const nextFields = mappings
    .filter((m) => m.mapping_type === "response")
    .map((m) =>
      mapBodyField(m.title, m.key, m.value, m.target_type, m.value_type)
    );

  return {
    step_type: step.step_type ?? "",
    step_name: step.name ?? "",
    content_type: step.content_type ?? "",
    method: step.method ?? "",
    endpoint_provisioning: step.endpoint ?? "",
    body_fill_customer: fillCustomer.length
      ? fillCustomer
      : [
          {
            title: "",
            key: "",
            value: "",
            target_type: "body",
            data_type: "string"
          }
        ],
    body_static_system: staticSystem.length
      ? staticSystem
      : [
          {
            title: "",
            key: "",
            value: "",
            target_type: "body",
            data_type: "string"
          }
        ],
    body_static_value: staticValue.length
      ? staticValue
      : [
          {
            title: "",
            key: "",
            value: "",
            target_type: "body",
            data_type: "string"
          }
        ],
    next_endpoint_fields: nextFields.length
      ? nextFields
      : [
          {
            title: "",
            key: "",
            value: "",
            target_type: "body",
            data_type: "string"
          }
        ]
  };
};

export const mapDetailToFormDefaults = (
  detail: AutomationEditData
): AutomationFormSchema => {
  const steps = detail.steps ?? [];

  const authTypeMap: Record<string, string> = {
    custom_header:
      detail.auth_header_name === "Authorization" ? "basic" : "x-header-secret",
    bearer: "bearer"
  };

  const endpoints = steps.length
    ? steps.map(buildEndpointFromStep)
    : [
        {
          step_type: "",
          step_name: "",
          content_type: "",
          method: "",
          endpoint_provisioning: "",
          body_fill_customer: [
            {
              title: "",
              key: "",
              value: "",
              target_type: "body",
              data_type: "string"
            }
          ],
          body_static_system: [
            {
              title: "",
              key: "",
              value: "",
              target_type: "body",
              data_type: "string"
            }
          ],
          body_static_value: [
            {
              title: "",
              key: "",
              value: "",
              target_type: "body",
              data_type: "string"
            }
          ],
          next_endpoint_fields: [
            {
              title: "",
              key: "",
              value: "",
              target_type: "body",
              data_type: "string"
            }
          ]
        }
      ];

  return {
    product_id: detail.product_id,
    product_name: detail.product_name,
    host: detail.host,
    host_auth: detail.host_auth ?? "",
    auth_type: authTypeMap[detail.auth_type] ?? detail.auth_type,
    auth_header_name: detail.auth_header_name,
    auth_value: detail.auth_value_prefix,
    token_response_field: detail.token_response_field ?? "",
    delivery_type: steps.length > 1 ? "multiple" : "single",
    endpoints
  };
};
